import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/Button/NewButton';
import { Header } from './Header';
import { ViewTickets } from './ViewTickets';
import { useSupportTicket } from '../../hooks/SupportTicket/useSupportTicket';
import { useOperator } from '../../contexts/OperatorContext';
import { useAppView } from '../../contexts/AppViewContext';
import { useUser } from '../../contexts/UserContext';
import { NoDataMessage } from '../../components/NoDataMessage';
import { NoDataFound } from '../Manage/Inventory/Infrastructure/components/NoDataFound';
import { LoadingWrapper } from '../Manage/Inventory/Infrastructure/components/LoadingWrapper';

export const SupportPage = () => {
  const operator = useOperator();
  const appView = useAppView();

  if (
    operator.isChanging ||
    (appView.type === 'Operator' &&
      (Number(appView.id) !== operator.id || appView.sector !== operator.type))
  )
    return <SupportLoading />;

  return <SupportTicketsPage key={`${operator.type}:${operator.id}`} />;
};

const SupportLoading = () => (
  <div role="status" aria-live="polite">
    <LoadingWrapper loading title="Loading Support Tickets" height={400} />
  </div>
);

const SupportTicketsPage = () => {
  const navigate = useNavigate();
  const [activeSite, setActiveSite] = useState<string | null>(null);
  const { getTickets, loading, tickets, error, categories, isSuccess } =
    useSupportTicket();
  const [hasLoadedTickets, setHasLoadedTickets] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(),
    end: new Date(),
  });
  const [pendingOnly, setPendingOnly] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [updateSubmitted, setUpdateSubmitted] = useState(false);
  const user = useUser();
  const operator = useOperator();

  useEffect(() => {
    if (!loading && isSuccess) setHasLoadedTickets(true);
  }, [loading, isSuccess]);

  const filteredTickets = selectedCategory
    ? tickets.filter((ticket) => ticket.categoryName === selectedCategory)
    : tickets;

  useEffect(() => {
    const timeout = setTimeout(
      () => {
        getTickets(
          pendingOnly,
          new Date(dateRange.start.toISOString()),
          new Date(dateRange.end.toISOString()),
          activeSite
        );
        setUpdateSubmitted(false);
      },
      updateSubmitted ? 4000 : 0
    );
    return () => clearTimeout(timeout);
  }, [
    operator.id,
    operator.type,
    dateRange,
    pendingOnly,
    activeSite,
    updateSubmitted,
  ]);

  if (
    (operator.type === 'Residential' && !user.permissions.canResSupportView) ||
    (operator.type === 'Hospitality' && !user.permissions.canHosSupportView)
  )
    return (
      <NoDataMessage
        title="No Access"
        subTitle="You do not have permission to view Support Tickets"
      />
    );

  if (!hasLoadedTickets && (loading || !error)) return <SupportLoading />;

  if (error && !loading)
    return (
      <div role="alert" className="p-6 w-full text-center break-words">
        <NoDataFound
          trigger
          title="Unable to Load Tickets"
          message={String(error.data || error.statusText)}
          height={400}
          actions={[
            <Button
              key="home"
              theme="default"
              typeCasing="capitalize"
              onClick={() => navigate('/')}
            >
              Home
            </Button>,
          ]}
        />
      </div>
    );

  const handleCreateTicket = () => {
    getTickets(
      pendingOnly,
      new Date(dateRange.start.toISOString()),
      new Date(dateRange.end.toISOString()),
      activeSite
    );
  };

  return (
    <div className="flex flex-col items-center justify-evenly">
      <Header onCreateTicket={handleCreateTicket} />
      <ViewTickets
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        ticketData={filteredTickets}
        loading={loading}
        setActiveSite={setActiveSite}
        dateRange={dateRange}
        setDateRange={setDateRange}
        pendingOnly={pendingOnly}
        setViewAll={setPendingOnly}
        updateSubmitted={updateSubmitted}
        setUpdateSubmitted={setUpdateSubmitted}
      />
    </div>
  );
};
